import React, {useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {useAuth} from "../../AuthContext";
import {useNotifications} from "../../components/NotificationSystem";

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    displayName: "",
    password: "",
    confirmPassword: "",
    accountType: "player" as "player" | "dm",
  });
  const [isLoading, setIsLoading] = useState(false);
  const {signUp, signInWithGoogle, clearError, updateLocalUserState} =
    useAuth();
  const {addNotification} = useNotifications();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      addNotification({
        type: "error",
        title: "Password Mismatch",
        message: "Passwords do not match. Please try again.",
        duration: 5000,
      });
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      addNotification({
        type: "error",
        title: "Weak Password",
        message: "Password must be at least 6 characters long.",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await signUp(
        formData.email,
        formData.password,
        formData.displayName,
        formData.accountType
      );
      // Optimistically update local state to avoid header email fallback flicker
      updateLocalUserState({displayName: formData.displayName});

      // Set registration flag to help EmailVerification component handle race conditions
      localStorage.setItem("justRegistered", "true");
      localStorage.setItem("registrationEmail", formData.email);

      navigate("/auth/email-verification");
    } catch (error: any) {
      // Use notification system for error display
      addNotification({
        type: "error",
        title: "Registration Failed",
        message:
          error.message ||
          "An error occurred during registration. Please try again.",
        duration: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    clearError();
    setIsLoading(true);

    try {
      // Use the account type from the form
      await signInWithGoogle(formData.accountType);
      // Show redirecting/setup page for first-time Google registrations
      navigate("/auth/redirecting");
    } catch (error: any) {
      // Use notification system for error display
      addNotification({
        type: "error",
        title: "Google Registration Failed",
        message:
          error.message ||
          "An error occurred during Google registration. Please try again.",
        duration: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAccountTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      accountType: e.target.value as "player" | "dm",
    }));
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Create Account</h1>
        <p>
          Create your League of Fabled Pursuits account and begin your adventure
        </p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Choose a display name"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password (min 6 characters)"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
              disabled={isLoading}
            />
            {formData.password &&
              formData.confirmPassword &&
              formData.password !== formData.confirmPassword && (
                <div className="error-message">Passwords do not match</div>
              )}
          </div>

          <div className="form-group">
            <label>Account type</label>
            <div className="account-type-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="accountType"
                  value="player"
                  checked={formData.accountType === "player"}
                  onChange={handleAccountTypeChange}
                  required
                  disabled={isLoading}
                />
                Player
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="accountType"
                  value="dm"
                  checked={formData.accountType === "dm"}
                  onChange={handleAccountTypeChange}
                  required
                  disabled={isLoading}
                />
                DM
              </label>
            </div>
            <div className="dm-notice">
              DM accounts require approval by an administrator before you can
              access DM features. This process typically takes less than a day,
              but please be patient if we're busy. You'll receive an email
              notification once your account has been approved.
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary register-button"
            disabled={
              isLoading ||
              formData.password !== formData.confirmPassword ||
              formData.password.length < 6
            }
          >
            {isLoading ? "Creating Account..." : "Sign up"}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="google-button-container">
          <button
            type="button"
            className="btn btn-google"
            onClick={handleGoogleSignUp}
            disabled={isLoading}
          >
            <svg className="google-icon" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? "Creating Account..." : "Sign up with Google"}
          </button>
        </div>

        <p className="auth-link">
          Already have an account? <Link to="/auth/login">Sign in here</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
